import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { Phone, Edit as EditIcon } from '@mui/icons-material';
import { Typography, Grid, Tooltip, IconButton } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import Label from 'components/display/label';
import { togglePhonePanel, toggleOngoingPhoneCall, setPhoneNoteTags } from 'actions/action-view';
import { updateNoteBar, openCommunicationNotesSideBar } from 'actions/action-notes';
import { NoteTagTypes } from 'constants/note-tag-types';
import { NeedInterpreterIcon } from 'containers/patient/patient-overview/components/need-interpreter-icon';
import EditPhonesSmsPreferencesModal, {
  EditModalViewModeEnum,
} from 'components/edit-phones-sms-preferences-modal/edit-phones-sms-preferences-modal';
import { IContactListItem } from 'interfaces/redux/IContactList';
import { IState } from 'interfaces/redux/IState';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { SMSIcon } from 'components/icons/icons';
import { buildQaId } from 'utils/build-qa-id';
import { PhoneUtils } from 'utils/phone-utils';
import { InterpreterService } from 'clients/interpreter-service';
import { IInterpreterService } from 'models/services/interpreter-service/IInterpreterService';
import { NoteBarTrigger } from 'constants/enums';
import InterpreterNumbersModal from 'components/interpreter-numbers-modal';
import { styles } from './talkdesk-phone-number.styles';
import { IProps } from './IProps';

const TalkDeskPhoneNumber: FC<IProps> = (props: IProps): JSX.Element => {
  const {
    classes,
    number: phoneNumber,
    headerText,
    additionalInlineText,
    availability,
    owner,
    tagInfo,
    preferSms,
    isInsurancePhone,
    useOpenPanel = false,
  } = props;
  const { respectInterpreter = false } = props;
  const [interpreterService] = React.useState<InterpreterService>(new InterpreterService());

  const patientNeedInterpreter = useTypedSelector(
    (state: IState) => state.patient?.need_interpreter,
  );
  const patientLanguages = useTypedSelector((state: IState) => state.patient?.languages);
  const needInterpreter = owner?.need_interpreter ?? patientNeedInterpreter;
  const interpreterEnabled = respectInterpreter && !!needInterpreter;
  const [interpreterServicePhoneNumbers, setInterpreterServicePhoneNumbers] = React.useState<
    IInterpreterService[]
  >([]);
  const [visibleEditPhonesModal, setEditPhonesModal] = React.useState<boolean>(false);
  const [openInterpreterModal, setOpenInterpreterModal] = React.useState(false);

  const getQaId = buildQaId('preferred-contact-phone');

  const dispatch = useDispatch();
  const currentPatientId = useTypedSelector((state: IState) => state.selectedPatientId);
  const tagInfoToPass = tagInfo || {
    tagTypeId: NoteTagTypes.Patient,
    tagResourceId: currentPatientId,
  };
  const isPhoneAvailable = PhoneUtils.isPhoneAvailable(availability);

  React.useEffect(() => {
    (async () => {
      if (needInterpreter && currentPatientId) {
        const interpreterServicePhones = await interpreterService.getInterpreterService(
          currentPatientId,
        );
        if (interpreterServicePhones && interpreterServicePhones.phoneNumbers) {
          if (
            interpreterServicePhones.phoneNumbers.some(
              (item: IInterpreterService) => !item.service_group_name,
            )
          ) {
            setInterpreterServicePhoneNumbers(
              interpreterServicePhones.phoneNumbers.filter(
                (item: IInterpreterService) => !item.service_group_name,
              ),
            );
          } else {
            setInterpreterServicePhoneNumbers(interpreterServicePhones.phoneNumbers);
          }
        }
      }
    })();
  }, [currentPatientId, interpreterEnabled]);

  const handleClick = async () => {
    dispatch(updateNoteBar({ display: false }));
    if (useOpenPanel) {
      dispatch(togglePhonePanel(true));
      dispatch(toggleOngoingPhoneCall(true));
    }
    if (tagInfoToPass && tagInfoToPass.tagTypeId && tagInfoToPass.tagResourceId) {
      dispatch(setPhoneNoteTags(tagInfoToPass));
    }
  };
  const renderInterpreterIcon = () =>
    interpreterEnabled ? (
      <div
        className={classes.interpreterAbsoluteIcon}
        onClick={() => setOpenInterpreterModal(true)}
      >
        <NeedInterpreterIcon
          languages={owner?.languages || patientLanguages}
          interpreterServicePhoneNumbers={interpreterServicePhoneNumbers}
        />
      </div>
    ) : null;

  const renderCharmFront = () =>
    preferSms ? (
      <Tooltip title="SMS">
        <div className={classes.phoneInlineIconBackground}>
          <SMSIcon data-qa-id={getQaId('sms-icon')} className={classes.phoneInlineIcon} />
        </div>
      </Tooltip>
    ) : (
      <Tooltip title="Phone">
        <div className={classes.phoneInlineIconBackground}>
          <Phone data-qa-id={getQaId('phone-icon')} className={classes.phoneInlineIcon} />
        </div>
      </Tooltip>
    );

  const phoneText = `${additionalInlineText || ''}${phoneNumber} ${availability || ''}`;
  const placeHolder = (
    <Label
      charmFront={renderCharmFront()}
      className={classes.phoneLabel}
      labelName=""
      labelType="phone"
    />
  );

  const phoneCallDisabled = interpreterEnabled;
  const renderLink = () => {
    const isDisabled = phoneCallDisabled || !isPhoneAvailable;
    const tooltipText = phoneCallDisabled
      ? 'Calling disabled for this patient'
      : 'Out of Available Hours';

    return (
      <>
        <div className={classes.phoneWrapper}>
          {!isDisabled ? (
            <a
              href={PhoneUtils.convertToTalkdeskCallableFormat(phoneNumber)}
              onClick={() => handleClick()}
              className={classes.phoneAnchor}
            >
              <Label
                labelName={` ${phoneText}`}
                noMaxWidth
                charmFront={renderCharmFront()}
                labelType="phone"
              />
            </a>
          ) : (
            <Tooltip title={tooltipText}>
              <span className={classes.phoneAnchor} style={{ cursor: 'not-allowed' }}>
                <span style={{ pointerEvents: 'none' }}>
                  <Label
                    labelName={` ${phoneText}`}
                    noMaxWidth
                    charmFront={renderCharmFront()}
                    labelType="disabled-phone"
                  />
                </span>
              </span>
            </Tooltip>
          )}
          {renderInterpreterIcon()}
        </div>
      </>
    );
  };

  return (
    <>
      <div>
        <Grid container align-items="center">
          {headerText && (
            <Typography variant="caption" className={classes.detailHeader}>
              {headerText}
            </Typography>
          )}
        </Grid>
        {phoneNumber ? renderLink() : placeHolder}

        {!(owner as IContactListItem)?.relationship && !isInsurancePhone && (
          <IconButton
            onClick={() => {
              setEditPhonesModal(true);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {visibleEditPhonesModal && !isInsurancePhone && (
          <EditPhonesSmsPreferencesModal
            visible={visibleEditPhonesModal}
            viewMode={EditModalViewModeEnum.EditPhone}
            onModalClose={() => {
              setEditPhonesModal(false);
            }}
          />
        )}
      </div>

      <InterpreterNumbersModal
        open={openInterpreterModal}
        onClose={() => setOpenInterpreterModal(false)}
        phoneNumbers={interpreterServicePhoneNumbers}
      />
    </>
  );
};

export default withStyles(styles)(TalkDeskPhoneNumber);
